import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Box, IconButton, Stack, Tooltip, Divider } from '@mui/material';
import {
  FormatBoldRounded,
  FormatItalicRounded,
  FormatListBulletedRounded,
  FormatListNumberedRounded,
  FormatQuoteRounded,
  CodeRounded,
  LinkRounded,
  ImageRounded,
  UndoRounded,
  RedoRounded,
  TitleRounded,
} from '@mui/icons-material';
import { useEffect } from 'react';

interface Props {
  value: Record<string, unknown> | null;
  onChange: (json: Record<string, unknown>) => void;
  placeholder?: string;
  /** Show a compact (single-line) toolbar variant. */
  compact?: boolean;
}

export function PageEditor({ value, onChange, placeholder, compact }: Props): JSX.Element {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      Placeholder.configure({ placeholder: placeholder ?? 'Escribe aquí…' }),
    ],
    content: (value as object) ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate({ editor }) {
      // ProseMirror builds node `attrs` with Object.create(null). The Firebase
      // callable serializer calls `o.hasOwnProperty(k)` directly, which throws
      // on null-prototype objects. Round-trip through JSON to get plain objects.
      onChange(JSON.parse(JSON.stringify(editor.getJSON())) as Record<string, unknown>);
    },
  });

  // Reset when value changes externally (e.g. switching pages).
  useEffect(() => {
    if (!editor || !value) return;
    const current = editor.getJSON();
    if (JSON.stringify(current) === JSON.stringify(value)) return;
    editor.commands.setContent(value as object, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return <Box />;
  }

  return (
    <Box
      sx={{
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar editor={editor} compact={compact} />
      <Divider />
      <Box
        sx={{
          p: 2,
          minHeight: compact ? 120 : 280,
          '& .ProseMirror': {
            outline: 'none',
            minHeight: compact ? 100 : 260,
            fontSize: '0.95rem',
            lineHeight: 1.6,
          },
          '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            float: 'left',
            pointerEvents: 'none',
            height: 0,
          },
          '& h1': { fontSize: '1.75rem', fontWeight: 700, my: 1.5 },
          '& h2': { fontSize: '1.35rem', fontWeight: 700, my: 1.25 },
          '& h3': { fontSize: '1.1rem', fontWeight: 700, my: 1 },
          '& p': { my: 0.75 },
          '& ul, & ol': { pl: 3 },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'divider',
            pl: 2,
            color: 'text.secondary',
            my: 1,
          },
          '& code': {
            bgcolor: 'action.hover',
            px: 0.5,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.88em',
          },
          '& pre': {
            bgcolor: 'action.hover',
            p: 1.5,
            borderRadius: 1,
            overflow: 'auto',
          },
          '& a': { color: 'primary.main', textDecoration: 'underline' },
          '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}

function Toolbar({ editor, compact }: { editor: Editor; compact?: boolean | undefined }) {
  const btn = (active: boolean, title: string, icon: JSX.Element, onClick: () => void) => (
    <Tooltip key={title} title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{ color: active ? 'primary.main' : 'text.secondary' }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  const promptLink = () => {
    const prev = editor.getAttributes('link')['href'] as string | undefined;
    const url = window.prompt('URL del enlace', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const promptImage = () => {
    const url = window.prompt('URL de la imagen');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <Stack
      direction="row"
      spacing={0.25}
      sx={{
        flexWrap: 'wrap',
        p: 0.5,
        bgcolor: 'action.hover',
        rowGap: 0.5,
      }}
    >
      {btn(editor.isActive('heading', { level: 1 }), 'H1', <TitleRounded fontSize="small" />, () =>
        editor.chain().focus().toggleHeading({ level: 1 }).run(),
      )}
      {!compact &&
        btn(
          editor.isActive('heading', { level: 2 }),
          'H2',
          <TitleRounded fontSize="small" sx={{ transform: 'scale(0.85)' }} />,
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        )}
      {btn(editor.isActive('bold'), 'Negrita', <FormatBoldRounded fontSize="small" />, () =>
        editor.chain().focus().toggleBold().run(),
      )}
      {btn(editor.isActive('italic'), 'Cursiva', <FormatItalicRounded fontSize="small" />, () =>
        editor.chain().focus().toggleItalic().run(),
      )}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      {btn(
        editor.isActive('bulletList'),
        'Lista',
        <FormatListBulletedRounded fontSize="small" />,
        () => editor.chain().focus().toggleBulletList().run(),
      )}
      {btn(
        editor.isActive('orderedList'),
        'Lista numerada',
        <FormatListNumberedRounded fontSize="small" />,
        () => editor.chain().focus().toggleOrderedList().run(),
      )}
      {btn(editor.isActive('blockquote'), 'Cita', <FormatQuoteRounded fontSize="small" />, () =>
        editor.chain().focus().toggleBlockquote().run(),
      )}
      {btn(editor.isActive('codeBlock'), 'Bloque de código', <CodeRounded fontSize="small" />, () =>
        editor.chain().focus().toggleCodeBlock().run(),
      )}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      {btn(editor.isActive('link'), 'Enlace', <LinkRounded fontSize="small" />, promptLink)}
      {btn(false, 'Imagen por URL', <ImageRounded fontSize="small" />, promptImage)}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      {btn(false, 'Deshacer', <UndoRounded fontSize="small" />, () =>
        editor.chain().focus().undo().run(),
      )}
      {btn(false, 'Rehacer', <RedoRounded fontSize="small" />, () =>
        editor.chain().focus().redo().run(),
      )}
    </Stack>
  );
}

// ─── Read-only viewer ────────────────────────────────────────────────────────

export function PageViewer({ value }: { value: Record<string, unknown> | null }): JSX.Element {
  const editor = useEditor({
    editable: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: true, autolink: true }),
      Image,
    ],
    content: (value as object) ?? { type: 'doc', content: [{ type: 'paragraph' }] },
  });

  useEffect(() => {
    if (!editor || !value) return;
    editor.commands.setContent(value as object, { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return <Box />;
  return (
    <Box
      sx={{
        '& .ProseMirror': { outline: 'none', lineHeight: 1.6 },
        '& h1': { fontSize: '1.85rem', fontWeight: 700, my: 1.5 },
        '& h2': { fontSize: '1.4rem', fontWeight: 700, my: 1.25 },
        '& h3': { fontSize: '1.15rem', fontWeight: 700, my: 1 },
        '& p': { my: 0.75 },
        '& ul, & ol': { pl: 3 },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'divider',
          pl: 2,
          color: 'text.secondary',
          my: 1,
        },
        '& a': { color: 'primary.main' },
        '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
        '& code': {
          bgcolor: 'action.hover',
          px: 0.5,
          borderRadius: 0.5,
          fontFamily: 'monospace',
        },
        '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto' },
      }}
    >
      <EditorContent editor={editor} />
    </Box>
  );
}
